import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AnswerModel } from '../models/answer.model';
import { DocumentModel } from '../models/document.model';
import { ObjectModel, TokenDimIndexModel } from '../models/object.model';
import { ApiService } from './api.service';
import { LogicResultModel } from '../models/logic.model';
import { MaskitoService } from './maskito.service';
import { Maskito, MaskitoOptions } from '@maskito/core';

@Injectable()
export class DocumentDataService {
  private praSysId: number;
  private templateId: number;
  private ctcId: string;
  private casId: string;
  private answers: Array<AnswerModel>;
  private objects: Map<string, Array<ObjectModel>>;
  private logicResults: Array<LogicResultModel>;
  private changedAnswerTokens: Array<string>;

  constructor(private api: ApiService, private maskitoService: MaskitoService) {
    this.answers = new Array<AnswerModel>();
    this.objects = new Map<string, Array<ObjectModel>>();
    this.logicResults = new Array<LogicResultModel>();
    this.praSysId = 0;
    this.templateId = 0;
    this.ctcId = '';
    this.casId = '';
    this.changedAnswerTokens = new Array<string>();
  }

  clearAll(): void {
    this.answers = new Array<AnswerModel>();
    this.objects = new Map<string, Array<ObjectModel>>();
    this.logicResults = new Array<LogicResultModel>();
    this.praSysId = 0;
    this.templateId = 0;
    this.ctcId = '';
    this.casId = '';
  }

  getAnswers(): Array<AnswerModel> {
    return this.answers;
  }

  setAnswers(answers: Array<AnswerModel>): void {
    if (answers != null && answers.length > 0) this.answers = answers;
  }

  updateAnswers(answers: Array<AnswerModel>): void {
    this.changedAnswerTokens = [];

    answers.forEach((a) => {
      const storedAnswer = this.getStoredAnswer(a.token, a.dimIndex);
      if (storedAnswer != null) {
        if (storedAnswer.answer !== a.answer) {
          this.changedAnswerTokens.push(a.token);
          storedAnswer.answer = a.answer;
        }
      } else {
        this.answers.push(a);
        this.changedAnswerTokens.push(a.token);
      }
    });
  }

  updateAnswer(token: string, answer: string) {
    if (!token.startsWith('Q')) {
      let o = this.getObject(token);
      if (o != null && o.qToken != null && o.qToken !== '') {
        token = o.qToken;
      }
    }
    const storedAnswer = this.getStoredAnswer(token);
    if (storedAnswer) {
      if (storedAnswer.answer !== answer) {
        storedAnswer.answer = answer;
      }
    } else {
      this.addAnswer(token, answer);
    }
  }

  addAnswer(token: string, answer: string) {
    const tokenAndDimIndex = this.getTokenAndDimIndex(token);
    const answerModel: AnswerModel = {
      token: tokenAndDimIndex.token,
      dimIndex: tokenAndDimIndex.dimIndex,
      answer: answer,
      answerChoice: '',
      praSysId: this.getPraSysId(),
    };
    this.answers.push(answerModel);
  }

  getLogicResults(): Array<LogicResultModel> {
    return this.logicResults;
  }

  getLogicResultByToken(token: string): LogicResultModel | undefined {
    return this.logicResults.find((l) => l.token === token);
  }

  getAnswerByToken(token: string): AnswerModel | undefined {
    //we cannot ignore the dim index here - we must return the correct answer for the field the user is operating in or we will bleed into other indexed answers on the same token
    const tokenWithIndex = this.getTokenAndDimIndex(token);
    return this.answers.find((a) => a.token === tokenWithIndex.token && a.dimIndex === tokenWithIndex.dimIndex);
  }

  setLogicResults(logicResults: Array<LogicResultModel>) {
    this.logicResults = logicResults;
  }

  updateLogicResults(newValues: LogicResultModel[]) {
    if (!newValues || newValues.length === 0) return;

    newValues.forEach((lr) => {
      const result = this.logicResults.find((r) => r.token === lr.token);
      if (result) {
        result.result = lr.result;
        result.actions = lr.actions;
      } else {
        this.logicResults.push(lr);
      }
    });
  }

  getObjects(): Map<string, Array<ObjectModel>> {
    console.log(`getObjects is returning ${JSON.stringify(this.objects)}`);
    return this.objects;
  }

  getObjectsByKey(key: string): Array<ObjectModel> {
    console.log(`getObjectsByKey is returning ${JSON.stringify(this.objects)}`);
    return this.objects.get(key) ?? new Array<ObjectModel>();
  }

  getObject(token: string): ObjectModel | null {
    const key = token.substring(0, 1);
    if (key === 'Q') {
      token = token.substring(0, 7); // remove dimIndex before searching

      if (this.objects.has(key)) {
        const o = this.objects.get(key)?.find((o) => o.token === token);

        if (o) return o;
      }
    } else {
      let tokenAndDimIndex = this.getTokenAndDimIndex(token);

      if (this.objects.has(key) && tokenAndDimIndex) {
        const o = this.objects
          .get(key)
          ?.find((o) => o.token === tokenAndDimIndex.token && o.dimIndex === tokenAndDimIndex.dimIndex);

        if (o) return o;
      }
    }

    return null;
  }

  setObjects(objects: Map<string, Array<ObjectModel>>): void {
    this.objects = new Map(Object.entries(objects));

    //create mask and apply input type for DOM manipulation
    this.objects.forEach((objectArray, key) => {
      if (key.includes("Q")) {
        objectArray.forEach((obj) => {
          if (obj.mask == "" || obj.mask == null) { //apply universal mask to allow all inputs
            obj.inputType = 'text';
            obj.maskitoMask = {
              mask: /.*/
            } as MaskitoOptions;
          } else {
            obj.maskitoMask = this.maskitoService.createMaskitoMask(obj.mask);
            if (obj.mask.toUpperCase().includes('D')) {
                obj.inputType = 'date'
            } else if (obj.mask.toUpperCase().includes('N')) {
              obj.inputType = 'number';
            } else if (obj.mask === '##/##/##' || obj.mask === '##/##/####') {
              obj.inputType = 'date';
            } else {
              obj.inputType = 'text'; 
            }
          }
        });
      }
    });
  }

  setObjectsByKey(key: string, objects: Array<ObjectModel>): void {
    this.objects.set(key, objects);
  }

  getStoredAnswer(formFieldName: string, dimIndex: string = ''): AnswerModel | undefined {
    let token = formFieldName;
    const dimIndexStart = formFieldName.indexOf('[');
    if (dimIndexStart > -1) {
      token = formFieldName.substring(0, dimIndexStart);
      const dimIndexEnd = formFieldName.indexOf(']');
      dimIndex = formFieldName.substring(dimIndexStart + 1, dimIndexEnd);
    }
    const answer = this.answers.find((a) => a.token === token && a.dimIndex === dimIndex);
    return answer;
  }

  getStoredLogicResult(token: string): LogicResultModel | undefined {
    const result = this.logicResults.find((l) => l.token === token);
    return result;
  }

  isAnswerChanged(formFieldName: string, value: string) {
    const o = this.getObject(formFieldName);

    if (o?.objType === 'I' && o.qToken != null && o.qToken !== '') {
      formFieldName = o.qToken;
    }
    let answer = null;
    if (formFieldName.startsWith('Q')) {
      answer = this.getStoredAnswer(formFieldName)?.answer;
    } else {
      answer = this.getStoredLogicResult(formFieldName)?.result;
    }

    if (answer == null) {
      return value !== '';
    } else {
      return answer !== value;
    }
  }

  getAnswersFromPool(document: DocumentModel) {
    return this.api.post('document/getanswers', document, { showToast: false });
  }

  saveAnswerToPool(answer: AnswerModel) {
    return this.api.post('document/saveanswer', { answer: answer }, { showToast: false });
  }

  setPraSysId(praSysId: number) {
    this.praSysId = praSysId;
  }

  getPraSysId(): number {
    return this.praSysId;
  }

  setTemplateId(templateId: number) {
    this.templateId = templateId;
  }

  getTemplateId(): number {
    return this.templateId;
  }

  setCtcId(ctcId: string) {
    this.ctcId = ctcId;
  }

  getCtcId(): string {
    return this.ctcId;
  }

  setCasId(casId: string) {
    this.casId = casId;
  }

  getCasId(): string {
    return this.casId;
  }

  public getIcodesToBeUpdated(): Array<ObjectModel> {
    const answers = this.changedAnswerTokens.map((t) => t.substring(0, 5));
    var objects = this.getObjectsByKey('I').filter((o) => answers.some((a) => o.logic.includes(a)));

    return objects;
  }

  private getTokenAndDimIndex(tokenWithDimIndex: string): TokenDimIndexModel {
    let token: string = tokenWithDimIndex;
    let dimIndex: string = '';
    const dimIndexStart = tokenWithDimIndex.indexOf('[');
    if (dimIndexStart > -1) {
      token = tokenWithDimIndex.substring(0, dimIndexStart);
      const dimIndexEnd = tokenWithDimIndex.indexOf(']');
      dimIndex = tokenWithDimIndex.substring(dimIndexStart + 1, dimIndexEnd);
    }

    return { token: token, dimIndex: dimIndex };
  }
}
